import IconButton from '@/components/common/IconButton';
import { useMetadata } from '@/hooks/useMetadata';
import { QueueItem } from '@/types/queue';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { FC, useState } from 'react';
import QueueMessagesDialog from './QueueMessagesDialog';

type QueueListItemEditProps = {
  queueItem?: QueueItem;
  onClose: (isEmpty?: boolean) => void;
  onSave: (newQueueItem: QueueItem) => void;
};

const QueueListItemEdit: FC<QueueListItemEditProps> = (props) => {
  const { metadata } = useMetadata();

  const [openDialog, setOpenDialog] = useState(false);
  const [queue, setQueue] = useState(props.queueItem?.queue || '');
  const [queueDescription, setQueueDescription] = useState(props.queueItem?.description || '');
  const [queueMessages, setQueueMessages] = useState<string[]>(props.queueItem?.messages || []);
  const [queueDatatype, setQueueDatatype] = useState(props.queueItem?.datatype || '');
  const valid: boolean = !!queue && !!queueDatatype;

  const handleClose = (isEmpty?: boolean) => {
    setQueue('');
    props.onClose(isEmpty);
  };

  const queueMessageOptions = ['BUSY', 'NOANSWER', 'ERROR'];

  return (
    <Stack
      direction='row'
      gap={1}
      pt={1}
      width='100%'
      alignItems='center'
      alignContent='flex-start'
      bgcolor='action.focus'
      borderRadius={2}
      p={1}
    >
      <Stack gap={1} flex={1} width='100%'>
        <TextField
          label='Queue'
          value={queue}
          onChange={(event) => setQueue(event.currentTarget.value.trim())}
          fullWidth
          size='small'
          autoFocus
          required
          InputProps={{ autoComplete: 'off' }}
        />
      </Stack>
      <Stack gap={1} flex={2} width='100%'>
        <TextField
          label='Description'
          value={queueDescription}
          onChange={(event) => setQueueDescription(event.currentTarget.value)}
          fullWidth
          size='small'
          InputProps={{ autoComplete: 'off' }}
        />
      </Stack>
      <Stack flex={1}>
        <Button variant='text' color='secondary' onClick={() => setOpenDialog(true)}>
          Messages
        </Button>
      </Stack>
      <FormControl fullWidth size='small' sx={{ flex: 1 }} required>
        <InputLabel id='state-select-datatype' required>
          Data Type
        </InputLabel>
        <Select
          labelId='state-select-datatype'
          id='state-select'
          value={queueDatatype || ''}
          label='Data type'
          onChange={(event) => setQueueDatatype(event.target.value)}
          required
        >
          {metadata.typelists
            ?.find((typelist) => typelist.name === 'queuedata')
            ?.types.map((type, index) => (
              <MenuItem key={`valuetype.${type}.${index}`} value={type}>
                {metadata.datatypes?.find((datatype) => datatype.type === type)?.label || type}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <IconButton
        tooltip={valid ? 'Save' : undefined}
        disabled={!valid}
        onClick={() => {
          if (!valid) return;
          const newQueueItem: QueueItem = {
            queue: queue,
            description: queueDescription,
            messages: queueMessages,
            datatype: queueDatatype
          };
          props.onSave(newQueueItem);
          handleClose();
        }}
      >
        <SaveIcon />
      </IconButton>
      <IconButton tooltip='Cancel' onClick={() => handleClose(!props.queueItem?.queue)}>
        <CancelIcon />
      </IconButton>
      <QueueMessagesDialog
        open={openDialog}
        messages={queueMessages}
        messageOptions={queueMessageOptions}
        onClose={(selectedMessages) => {
          setQueueMessages(selectedMessages);
          setOpenDialog(false);
        }}
      />
    </Stack>
  );
};

export default QueueListItemEdit;
